import React, { useMemo, useState } from 'react';
import { BTN_SECONDARY } from '../lib/buttonStyles';
import { parseUtc } from '../lib/billing';

/**
 * «Метрики» → «Организация»: расход всей организации за текущий период тарифа.
 * Видит только плательщик — решает UserMetrics, здесь проверки нет.
 *
 * 🔴 Числа — из той же ручки, что и раздел «Организация»
 * (`GET /organizations/{id}/usage`). Прогон 24.09: плательщик видел в «Метриках»
 * только себя, в разделе «Организация» — всех, и числа расходились. Второй
 * способ счёта разошёлся бы снова, поэтому своего счёта здесь нет.
 *
 * Разреза по дням нет намеренно (v1): ручка отдаёт итог за период тарифа, а
 * ряды по дням для организации — это новая агрегация в bit-metrics. Выбор
 * «14 / 30 дней» к расходу организации не применим, и экран это говорит, а не
 * подставляет период тарифа под чужую подпись.
 */

const CARD = 'bg-paper-card rounded-card px-6 py-[22px] flex flex-col justify-between';
const CAPTION = 'text-[12px] text-paper-ink-3';
const BIG = 'font-display font-extrabold leading-none tracking-[-0.04em] text-paper-ink whitespace-nowrap';

// bit-metrics отдаёт границы периода наивным UTC ('2026-10-01T00:00:00'): голый
// new Date прочёл бы его как местное время. parseUtc дописывает зону.
const fmtDay = (value) => {
  const d = parseUtc(value);
  return d ? d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '—';
};

const pct = (part, whole) => (whole ? Math.max(0, Math.min(100, Math.round((part / whole) * 100))) : 0);

/**
 * Что показывают карточки: вся организация или один человек.
 * Вынесено из разметки, чтобы фильтр по человеку проверялся счётом.
 */
export function orgCards(usage, person = null) {
  const members = usage.members || [];
  const limit = usage.minutes_limit ?? null;
  const total = usage.minutes_total || 0;
  if (person) {
    const row = members.find((m) => m.email === person) || { minutes: 0, summaries: 0 };
    return {
      person,
      minutes: Math.round(row.minutes),
      limit,
      protocols: row.summaries,
      share: pct(row.minutes, total),
      barPct: limit ? pct(row.minutes, limit) : null,
    };
  }
  return {
    person: null,
    minutes: Math.round(total),
    limit,
    protocols: members.reduce((s, m) => s + (m.summaries || 0), 0),
    // «Работали N из M» — оба числа по ОДНОМУ множеству: все, кто был в
    // организации в этом периоде (список ручки, включая исключённых, — та же
    // таблица «По людям» ниже). Прежде M брался только по действующим, а N
    // считал и исключённых с расходом — выходило «3 из 2».
    worked: members.filter((m) => m.minutes > 0 || m.summaries > 0).length,
    membersInPeriod: members.length,
    overage: limit ? Math.max(0, Math.round(total - limit)) : 0,
    barPct: limit ? pct(total, limit) : null,
  };
}

export default function OrgMetrics({ org, usage, onRetry }) {
  const [person, setPerson] = useState(null);
  const data = usage && usage.data;
  const cards = useMemo(() => (data ? orgCards(data, person) : null), [data, person]);

  if (usage && usage.error) {
    return (
      <div data-testid="org-metrics-error"
           className="rounded-panel bg-paper-accent/[0.06] px-4 py-3 text-[13px] text-paper-accent flex flex-wrap items-center gap-3">
        <span>{usage.error}</span>
        {usage.retry && <button type="button" onClick={onRetry} className={BTN_SECONDARY}>Повторить</button>}
      </div>
    );
  }
  if (!cards) {
    return <div className={`${CARD} ${CAPTION}`}>Загружаем расход организации…</div>;
  }

  const members = data.members || [];
  const maxMinutes = Math.max(1, ...members.map((m) => m.minutes));
  const togglePerson = (email) => setPerson((cur) => (cur === email ? null : email));

  return (
    <div data-testid="org-metrics" className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="grid gap-3 shrink-0 grid-cols-[minmax(0,1fr)] lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div className={CARD}>
          <div className={`${CAPTION} truncate`} title={person || undefined}>
            {person ? `Расход · ${person}` : 'Израсходовано из пакета'}
          </div>
          <div data-testid="org-card-minutes" className={`my-[10px] text-[56px] ${BIG}`}>
            {cards.minutes}
            <span className="ml-2 text-[20px] font-semibold tracking-[-0.02em] text-paper-ink-3">
              {person || cards.limit === null ? 'мин' : `из ${cards.limit} мин`}
            </span>
          </div>
          {cards.barPct !== null && (
            <div className="h-[6px] rounded-[3px] bg-paper-bg overflow-hidden">
              <div className="h-full bg-paper-accent" style={{ width: `${cards.barPct}%` }} />
            </div>
          )}
          <div className="mt-[10px] text-[12px] text-paper-ink-2">
            {person && (cards.limit ? `${cards.barPct} % пакета организации` : 'Пакет без лимита')}
            {!person && cards.overage > 0 && (
              <span className="text-paper-accent">
                Сверх пакета {cards.overage} мин — не списывается: после допуска 10 % распознавание остановится до продления
              </span>
            )}
            {!person && !cards.overage && (cards.limit === null
              ? 'Пакет без лимита'
              : `Осталось ${Math.max(0, cards.limit - cards.minutes)} мин до ${fmtDay(data.period_end)}`)}
          </div>
        </div>

        <div className={CARD}>
          <div className={CAPTION}>Протоколов</div>
          <div data-testid="org-card-protocols" className={`mt-[10px] text-[40px] ${BIG}`}>{cards.protocols}</div>
          <div className={`mt-2 ${CAPTION}`}>{person ? 'собрал за период' : 'всеми участниками за период'}</div>
        </div>

        {person ? (
          <div className={CARD}>
            <div className={CAPTION}>Доля в расходе</div>
            <div data-testid="org-card-third" className={`mt-[10px] text-[40px] ${BIG}`}>{cards.share} %</div>
            <div className={`mt-2 ${CAPTION}`}>от минут организации за период</div>
          </div>
        ) : (
          <div className={CARD}>
            <div className={CAPTION}>Работали в периоде</div>
            <div data-testid="org-card-third" className={`mt-[10px] text-[40px] ${BIG}`}>
              {cards.worked}
              <span className="ml-2 text-[18px] font-semibold tracking-[-0.02em] text-paper-ink-3">
                из {cards.membersInPeriod}
              </span>
            </div>
            <div className={`mt-2 ${CAPTION}`}>записывали или собирали протоколы</div>
          </div>
        )}

        <div className={CARD}>
          <div className={CAPTION}>Период тарифа</div>
          <div className={`mt-[10px] text-[28px] ${BIG}`}>
            {fmtDay(data.period_start)} – {fmtDay(data.period_end)}
          </div>
          {/* Выбор «14 / 30 дней» живёт в «Мои»: ручка считает расход только
              за период тарифа, другого диапазона у неё нет. */}
          <div className={`mt-2 ${CAPTION}`}>
            Считается за текущий период тарифа. Выбор «14 / 30 дней» — в «Мои».
          </div>
        </div>
      </div>

      <div className="bg-paper-card rounded-card px-6 py-[22px] flex flex-col gap-3 flex-1 min-h-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className={CAPTION}>По людям · организация «{org.name}»</span>
          <label className="flex items-center gap-2 text-[12px] text-paper-ink-2">
            Участник
            <select
              aria-label="Участник"
              value={person || ''}
              onChange={(e) => setPerson(e.target.value || null)}
              className="rounded-chip border border-paper-track bg-paper-card px-3 py-[6px] text-[13px] text-paper-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent"
            >
              <option value="">Все участники</option>
              {members.map((m) => <option key={m.email} value={m.email}>{m.email}</option>)}
            </select>
          </label>
        </div>

        {members.length === 0 ? (
          <div className="text-[13px] text-paper-ink-3">В этом периоде в организации никого не было.</div>
        ) : (
          <div className="flex flex-col min-h-0 overflow-auto">
            <div className={`grid grid-cols-[minmax(0,1fr)_80px_90px] sm:grid-cols-[minmax(0,1fr)_80px_90px_140px] gap-3 pb-2 ${CAPTION}`}>
              <span>Почта</span>
              <span className="text-right">Минуты</span>
              <span className="text-right">Протоколы</span>
              <span className="hidden sm:block" />
            </div>
            {members.map((m) => (
              <button
                key={m.email}
                type="button"
                aria-pressed={person === m.email}
                onClick={() => togglePerson(m.email)}
                className={`grid grid-cols-[minmax(0,1fr)_80px_90px] sm:grid-cols-[minmax(0,1fr)_80px_90px_140px] gap-3 items-center rounded-control px-2 -mx-2 py-2 text-left text-[13px] transition-colors ${
                  person === m.email ? 'bg-paper-panel' : 'hover:bg-paper-panel'
                }`}
              >
                <span className="truncate text-paper-ink" title={m.email}>
                  {m.email}
                  {!m.active && <span className="ml-2 text-[12px] text-paper-ink-3">исключён</span>}
                </span>
                <span className="text-right font-mono text-paper-ink">{Math.round(m.minutes)}</span>
                <span className="text-right font-mono text-paper-ink">{m.summaries}</span>
                <span className="hidden sm:block h-[6px] rounded-[3px] bg-paper-bg overflow-hidden">
                  <span className="block h-full bg-paper-ink" style={{ width: `${pct(m.minutes, maxMinutes)}%` }} />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
